import React, { useState, useCallback } from 'react';
import {
  Shield, Users, Key, Lock, ChevronRight, Plus, Search, MoreHorizontal,
  Edit2, Trash2, CheckCircle, XCircle, AlertTriangle, Eye, Copy, Clock,
  Settings, Globe, UserPlus, ShieldCheck, ShieldAlert, FileText, Zap,
  ChevronDown, ChevronUp, Info
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from '../../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger
} from '../../components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '../../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '../../components/ui/tooltip';
import { cn } from '../../lib/utils';

const mockRoles = [
  {
    id: 'role-1',
    name: 'Organization Admin',
    description: 'Full access to all organization settings, teams, and data',
    type: 'system',
    memberCount: 2,
    permissions: {
      'org.settings': true,
      'org.billing': true,
      'teams.create': true,
      'teams.delete': true,
      'teams.manage': true,
      'users.invite': true,
      'users.remove': true,
      'workflows.publish_org': true,
      'workflows.delete': true,
      'approvals.override': true,
      'audit.view': true,
      'audit.export': true
    }
  },
  {
    id: 'role-2',
    name: 'Team Manager',
    description: 'Manage team members, approve workflows, and handle escalations',
    type: 'system',
    memberCount: 8,
    permissions: {
      'teams.manage': true,
      'users.invite': true,
      'workflows.publish_team': true,
      'workflows.approve': true,
      'approvals.approve': true,
      'audit.view': true
    }
  },
  {
    id: 'role-3',
    name: 'IT Agent',
    description: 'Handle tickets, execute workflows, and access knowledge base',
    type: 'system',
    memberCount: 24,
    permissions: {
      'tickets.view': true,
      'tickets.respond': true,
      'tickets.close': true,
      'workflows.execute': true,
      'knowledge.view': true,
      'knowledge.suggest': true
    }
  },
  {
    id: 'role-4',
    name: 'Security Analyst',
    description: 'Review security events, manage incidents, access audit logs',
    type: 'custom',
    memberCount: 4,
    permissions: {
      'security.view': true,
      'security.respond': true,
      'audit.view': true,
      'audit.export': true,
      'workflows.execute': true,
      'approvals.approve': true
    }
  },
  {
    id: 'role-5',
    name: 'Read-Only Auditor',
    description: 'View-only access to audit logs and compliance reports',
    type: 'custom',
    memberCount: 2,
    permissions: {
      'audit.view': true,
      'compliance.view': true
    }
  }
];

const permissionCategories = [
  {
    id: 'organization',
    name: 'Organization',
    icon: Globe,
    permissions: [
      { id: 'org.settings', name: 'Manage Settings', description: 'Edit organization settings and preferences' },
      { id: 'org.billing', name: 'Manage Billing', description: 'View and manage billing, invoices, and plans' }
    ]
  },
  {
    id: 'teams',
    name: 'Teams',
    icon: Users,
    permissions: [
      { id: 'teams.create', name: 'Create Teams', description: 'Create new teams in the organization' },
      { id: 'teams.delete', name: 'Delete Teams', description: 'Delete teams and remove all members' },
      { id: 'teams.manage', name: 'Manage Team Settings', description: 'Edit team settings and configurations' }
    ]
  },
  {
    id: 'users',
    name: 'Users',
    icon: UserPlus,
    permissions: [
      { id: 'users.invite', name: 'Invite Users', description: 'Invite new users to the organization or team' },
      { id: 'users.remove', name: 'Remove Users', description: 'Remove users from the organization' }
    ]
  },
  {
    id: 'workflows',
    name: 'Workflows',
    icon: Zap,
    permissions: [
      { id: 'workflows.execute', name: 'Execute Workflows', description: 'Run and execute published workflows' },
      { id: 'workflows.publish_team', name: 'Publish (Team)', description: 'Publish workflows for team use' },
      { id: 'workflows.publish_org', name: 'Publish (Org-wide)', description: 'Publish workflows organization-wide' },
      { id: 'workflows.approve', name: 'Approve Workflows', description: 'Review and approve workflow submissions' },
      { id: 'workflows.delete', name: 'Delete Workflows', description: 'Delete workflows from the library' }
    ]
  },
  {
    id: 'tickets',
    name: 'Tickets',
    icon: FileText,
    permissions: [
      { id: 'tickets.view', name: 'View Tickets', description: 'View tickets assigned to team' },
      { id: 'tickets.respond', name: 'Respond to Tickets', description: 'Send replies and internal notes' },
      { id: 'tickets.close', name: 'Close Tickets', description: 'Mark tickets as resolved or closed' }
    ]
  },
  {
    id: 'approvals',
    name: 'Approvals',
    icon: CheckCircle,
    permissions: [
      { id: 'approvals.approve', name: 'Approve Requests', description: 'Approve or deny pending access requests' },
      { id: 'approvals.override', name: 'Override Approvals', description: 'Override existing approvals or denials' }
    ]
  },
  {
    id: 'security',
    name: 'Security',
    icon: ShieldAlert,
    permissions: [
      { id: 'security.view', name: 'View Security Events', description: 'View security alerts and incidents' },
      { id: 'security.respond', name: 'Respond to Incidents', description: 'Take action on security incidents' }
    ]
  },
  {
    id: 'audit',
    name: 'Audit & Compliance',
    icon: ShieldCheck,
    permissions: [
      { id: 'audit.view', name: 'View Audit Logs', description: 'View activity and audit logs' },
      { id: 'audit.export', name: 'Export Audit Data', description: 'Export audit logs and reports' },
      { id: 'compliance.view', name: 'View Compliance', description: 'View compliance reports and status' }
    ]
  },
  {
    id: 'knowledge',
    name: 'Knowledge Base',
    icon: FileText,
    permissions: [
      { id: 'knowledge.view', name: 'View Knowledge', description: 'Access knowledge base articles' },
      { id: 'knowledge.suggest', name: 'Suggest Articles', description: 'Suggest improvements to articles' }
    ]
  }
];

const mockApiKeys = [
  {
    id: 'key-1',
    name: 'Production Integration',
    prefix: 'ved_prod_',
    lastUsed: '2 hours ago',
    createdAt: '2024-01-15',
    createdBy: 'Emily Zhang',
    scopes: ['workflows.execute', 'tickets.view', 'tickets.respond']
  },
  {
    id: 'key-2',
    name: 'Slack Bot',
    prefix: 'ved_slack_',
    lastUsed: '5 minutes ago',
    createdAt: '2024-01-20',
    createdBy: 'David Park',
    scopes: ['tickets.view', 'tickets.respond', 'knowledge.view']
  },
  {
    id: 'key-3',
    name: 'SIEM Integration',
    prefix: 'ved_siem_',
    lastUsed: '1 day ago',
    createdAt: '2024-02-01',
    createdBy: 'Alex Kim',
    scopes: ['audit.view', 'audit.export', 'security.view']
  }
];

export const Permissions = () => {
  const [activeTab, setActiveTab] = useState('roles');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateRoleDialog, setShowCreateRoleDialog] = useState(false);
  const [showCreateKeyDialog, setShowCreateKeyDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showRoleDetailDialog, setShowRoleDetailDialog] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set(['organization', 'teams']));
  const [newRole, setNewRole] = useState({ name: '', description: '', permissions: {} });
  const [newApiKey, setNewApiKey] = useState({ name: '', scopes: [] });

  const toggleCategory = useCallback((categoryId) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  }, []);

  const filteredRoles = mockRoles.filter(role =>
    role.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    role.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleViewRole = (role) => {
    setSelectedRole(role);
    setShowRoleDetailDialog(true);
  };

  const stats = {
    totalRoles: mockRoles.length,
    customRoles: mockRoles.filter(r => r.type === 'custom').length,
    totalMembers: mockRoles.reduce((sum, r) => sum + r.memberCount, 0),
    apiKeys: mockApiKeys.length
  };

  return (
    <TooltipProvider>
      <div className="min-h-full bg-background">
        <div className="border-b bg-card">
          <div className="max-w-6xl mx-auto px-6 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Permissions</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Manage roles, permissions, and API access
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="grid grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-primary" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats.totalRoles}</p>
                    <p className="text-xs text-muted-foreground">Total Roles</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Settings className="w-5 h-5 text-blue-600" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats.customRoles}</p>
                    <p className="text-xs text-muted-foreground">Custom Roles</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-green-600" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats.totalMembers}</p>
                    <p className="text-xs text-muted-foreground">Users with Roles</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Key className="w-5 h-5 text-amber-600" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats.apiKeys}</p>
                    <p className="text-xs text-muted-foreground">API Keys</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <div className="flex items-center justify-between">
              <TabsList>
                <TabsTrigger value="roles">Roles</TabsTrigger>
                <TabsTrigger value="permissions">Permission Matrix</TabsTrigger>
                <TabsTrigger value="api-keys">API Keys</TabsTrigger>
              </TabsList>

              {activeTab === 'roles' && (
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
                    <Input
                      placeholder="Search roles..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 w-64"
                    />
                  </div>
                  <Dialog open={showCreateRoleDialog} onOpenChange={setShowCreateRoleDialog}>
                    <DialogTrigger asChild>
                      <Button>
                        <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
                        Create Role
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Create Custom Role</DialogTitle>
                        <DialogDescription>
                          Define a new role with specific permissions for your organization
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-6 py-4">
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="role-name">Role Name</Label>
                            <Input
                              id="role-name"
                              placeholder="e.g., Security Analyst"
                              value={newRole.name}
                              onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="role-description">Description</Label>
                            <Input
                              id="role-description"
                              placeholder="What can this role do?"
                              value={newRole.description}
                              onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                            />
                          </div>
                        </div>

                        <div className="space-y-4">
                          <Label>Permissions</Label>
                          {permissionCategories.map((category) => {
                            const CategoryIcon = category.icon;
                            return (
                              <div key={category.id} className="border rounded-lg">
                                <button
                                  className="w-full flex items-center justify-between p-3 hover:bg-muted/50"
                                  onClick={() => toggleCategory(category.id)}
                                >
                                  <div className="flex items-center gap-2">
                                    <CategoryIcon className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                                    <span className="font-medium text-sm">{category.name}</span>
                                  </div>
                                  {expandedCategories.has(category.id) ? (
                                    <ChevronUp className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                                  )}
                                </button>
                                {expandedCategories.has(category.id) && (
                                  <div className="px-3 pb-3 space-y-2">
                                    {category.permissions.map((perm) => (
                                      <label
                                        key={perm.id}
                                        className="flex items-start gap-3 p-2 rounded hover:bg-muted/30 cursor-pointer"
                                      >
                                        <Checkbox
                                          checked={newRole.permissions[perm.id] || false}
                                          onCheckedChange={(checked) =>
                                            setNewRole({
                                              ...newRole,
                                              permissions: { ...newRole.permissions, [perm.id]: checked }
                                            })
                                          }
                                        />
                                        <div>
                                          <p className="text-sm font-medium">{perm.name}</p>
                                          <p className="text-xs text-muted-foreground">{perm.description}</p>
                                        </div>
                                      </label>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreateRoleDialog(false)}>Cancel</Button>
                        <Button onClick={() => setShowCreateRoleDialog(false)}>Create Role</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              )}

              {activeTab === 'api-keys' && (
                <Dialog open={showCreateKeyDialog} onOpenChange={setShowCreateKeyDialog}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
                      Create API Key
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create API Key</DialogTitle>
                      <DialogDescription>
                        Generate a new API key for integrations
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="key-name">Key Name</Label>
                        <Input
                          id="key-name"
                          placeholder="e.g., Production Integration"
                          value={newApiKey.name}
                          onChange={(e) => setNewApiKey({ ...newApiKey, name: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Scopes</Label>
                        <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                          {permissionCategories.flatMap(cat => cat.permissions).map((perm) => (
                            <label
                              key={perm.id}
                              className="flex items-center gap-2 text-sm cursor-pointer"
                            >
                              <Checkbox
                                checked={newApiKey.scopes.includes(perm.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setNewApiKey({ ...newApiKey, scopes: [...newApiKey.scopes, perm.id] });
                                  } else {
                                    setNewApiKey({ ...newApiKey, scopes: newApiKey.scopes.filter(s => s !== perm.id) });
                                  }
                                }}
                              />
                              <span>{perm.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm text-amber-800">
                          <strong>Important:</strong> The API key will only be shown once. Store it securely.
                        </p>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowCreateKeyDialog(false)}>Cancel</Button>
                      <Button onClick={() => setShowCreateKeyDialog(false)}>Generate Key</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            <TabsContent value="roles" className="space-y-4">
              {filteredRoles.map((role) => (
                <Card key={role.id} className="hover:border-primary/50 transition-colors">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={cn(
                          "w-10 h-10 rounded-lg flex items-center justify-center shrink-0",
                          role.type === 'system' ? "bg-primary/10" : "bg-blue-100"
                        )}>
                          <Shield className={cn(
                            "w-5 h-5",
                            role.type === 'system' ? "text-primary" : "text-blue-600"
                          )} aria-hidden="true" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-base font-semibold text-foreground">{role.name}</h3>
                            <Badge variant="outline" className={cn(
                              "text-xs",
                              role.type === 'system' ? "bg-slate-100" : "bg-blue-50 text-blue-700 border-blue-200"
                            )}>
                              {role.type === 'system' ? 'System' : 'Custom'}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{role.description}</p>
                          <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Users className="w-3.5 h-3.5" aria-hidden="true" />
                              {role.memberCount} members
                            </span>
                            <span className="flex items-center gap-1">
                              <Key className="w-3.5 h-3.5" aria-hidden="true" />
                              {Object.keys(role.permissions).length} permissions
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleViewRole(role)}>
                          <Eye className="w-4 h-4 mr-2" aria-hidden="true" />
                          View
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <UserPlus className="w-4 h-4 mr-2" />
                              Assign Users
                            </DropdownMenuItem>
                            {role.type === 'custom' && (
                              <>
                                <DropdownMenuItem>
                                  <Edit2 className="w-4 h-4 mr-2" />
                                  Edit Role
                                </DropdownMenuItem>
                                <DropdownMenuItem>
                                  <Copy className="w-4 h-4 mr-2" />
                                  Duplicate
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem className="text-destructive">
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete Role
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            <TabsContent value="permissions">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Permission Matrix</CardTitle>
                  <CardDescription>
                    Overview of permissions across all roles
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase min-w-[200px]">Permission</th>
                          {mockRoles.map(role => (
                            <th key={role.id} className="text-center p-4 text-xs font-medium text-muted-foreground uppercase min-w-[100px]">
                              {role.name.split(' ').map(w => w[0]).join('')}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="w-3 h-3 inline ml-1 cursor-help" aria-hidden="true" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{role.name}</p>
                                </TooltipContent>
                              </Tooltip>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {permissionCategories.map((category) => (
                          <React.Fragment key={category.id}>
                            <tr className="bg-muted/30">
                              <td colSpan={mockRoles.length + 1} className="p-3">
                                <div className="flex items-center gap-2">
                                  <category.icon className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                                  <span className="font-medium text-sm text-foreground">{category.name}</span>
                                </div>
                              </td>
                            </tr>
                            {category.permissions.map((perm) => (
                              <tr key={perm.id} className="border-b hover:bg-muted/30">
                                <td className="p-4">
                                  <div>
                                    <p className="text-sm font-medium text-foreground">{perm.name}</p>
                                    <p className="text-xs text-muted-foreground">{perm.description}</p>
                                  </div>
                                </td>
                                {mockRoles.map(role => (
                                  <td key={role.id} className="p-4 text-center">
                                    {role.permissions[perm.id] ? (
                                      <CheckCircle className="w-5 h-5 text-green-500 mx-auto" aria-label="Granted" />
                                    ) : (
                                      <XCircle className="w-5 h-5 text-slate-300 mx-auto" aria-label="Not granted" />
                                    )}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="api-keys" className="space-y-4">
              {mockApiKeys.map((key) => (
                <Card key={key.id}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                          <Key className="w-5 h-5 text-amber-600" aria-hidden="true" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-base font-semibold text-foreground">{key.name}</h3>
                            <code className="px-2 py-0.5 bg-muted rounded text-xs text-muted-foreground">
                              {key.prefix}...
                            </code>
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                              Last used: {key.lastUsed}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-3.5 h-3.5" aria-hidden="true" />
                              Created by {key.createdBy}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1 mt-3">
                            {key.scopes.map((scope, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">{scope}</Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <Eye className="w-4 h-4 mr-2" />
                              View Scopes
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Edit2 className="w-4 h-4 mr-2" />
                              Edit Scopes
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Revoke Key
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              <Card className="border-dashed">
                <CardContent className="p-6 text-center">
                  <Key className="w-8 h-8 text-muted-foreground mx-auto mb-2" aria-hidden="true" />
                  <p className="text-sm text-muted-foreground mb-3">
                    API keys allow external systems to integrate with Vedaya securely.
                  </p>
                  <Button variant="outline" onClick={() => setShowCreateKeyDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
                    Create New API Key
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        <Dialog open={showRoleDetailDialog} onOpenChange={setShowRoleDetailDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" aria-hidden="true" />
                {selectedRole?.name}
              </DialogTitle>
              <DialogDescription>{selectedRole?.description}</DialogDescription>
            </DialogHeader>
            {selectedRole && (
              <div className="py-4 space-y-6">
                <div className="flex items-center gap-4">
                  <Badge variant="outline" className={cn(
                    selectedRole.type === 'system' ? "bg-slate-100" : "bg-blue-50 text-blue-700 border-blue-200"
                  )}>
                    {selectedRole.type === 'system' ? 'System Role' : 'Custom Role'}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {selectedRole.memberCount} members assigned
                  </span>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-foreground mb-3">Permissions</h4>
                  <div className="space-y-3">
                    {permissionCategories.map((category) => {
                      const activePerms = category.permissions.filter(p => selectedRole.permissions[p.id]);
                      if (activePerms.length === 0) return null;
                      const CategoryIcon = category.icon;
                      return (
                        <div key={category.id} className="p-3 border rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <CategoryIcon className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                            <span className="font-medium text-sm text-foreground">{category.name}</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {activePerms.map((perm) => (
                              <Badge key={perm.id} variant="secondary" className="text-xs">
                                <CheckCircle className="w-3 h-3 mr-1 text-green-500" aria-hidden="true" />
                                {perm.name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRoleDetailDialog(false)}>Close</Button>
              {selectedRole?.type === 'custom' && (
                <Button>
                  <Edit2 className="w-4 h-4 mr-2" aria-hidden="true" />
                  Edit Role
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default Permissions;
